import { useEffect, useState } from 'react';
import { Button } from '@skupreme/shared/ui';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  FormInputSuffix,
  MaskedNumberFormField,
  MuteLabel,
} from '@elements/FormField';
import { ToastMessage } from '@utility/toast';
import { ErrorMessageConstant } from '@utility/constants/common';
import { LoaderBar } from '@components/LoaderBar';
import PurchaseOrdersService from '@utility/services/purchase-order';
import { CommonUtility } from '@utility/common';
import { AppModal } from '@elements/Modal';

const shippableQtySchema = yup.object().shape({
  shippable_qty: yup
    .number()
    .required()
    .min(0)
    .when('$maxUnit', (maxUnit, schema) => schema.max(maxUnit)),
});
export function MarkAvailableUnitToShipPopup({ open, closeModal, poId, item }) {
  const [saving, setSaving] = useState('');
  const [maxUnit, setMaxUnit] = useState(200000000);
  const [minUnit, setMinUnit] = useState(0);

  const {
    formState: { errors },
    handleSubmit,
    control,
    setValue,
  } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    criteriaMode: 'all',
    resolver: yupResolver(shippableQtySchema),
    context: { maxUnit, minUnit },
    shouldUnregister: true,
  });

  useEffect(() => {
    const minUnit = open ? item?.shipped_qty || 0 : 0;
    const maxUnit =
      ((item?.aliases || []).reduce((a, b) => a + b.qty, 0) || 0) -
      (item?.shipped_qty || 0);
    // @ts-expect-error - TS2345: Argument of type '"shippable_qty"' is not assignable to parameter of type 'never...
    setValue('shippable_qty', (item.shippable_qty || 0) - minUnit);
    setMinUnit(minUnit);
    setMaxUnit(maxUnit);
  }, [open, item]);

  const onSubmit = async formData => {
    try {
      const bodyData = {
        items: [
          {
            item_upc: item.item_upc,
            shippable_qty: minUnit + formData.shippable_qty,
          },
        ],
      };
      setSaving('Saving');
      await PurchaseOrdersService.updateItemShippableQty(poId, bodyData);

      ToastMessage.success('Shippable Quantity has been saved successfully.');
      closeModal(true);
    } catch (error) {
      ToastMessage.error(error.error || ErrorMessageConstant.defaultAPIError);
    } finally {
      setSaving('');
    }
  };

  return (
    <AppModal
      open={open}
      title="Mark Units Available to Ship"
      closeModal={() => closeModal()}
    >
      {saving && <LoaderBar content={saving} />}
      <form
        autoComplete="off"
        noValidate
        onSubmit={handleSubmit(onSubmit)}
        className="sk:flex sk:flex-col"
      >
        <p className="sk:m-0 sk:text-sm sk:text-fg-secondary">
          Select a quantity that has already been produced. This quantity can
          then be allocated to a shipment before the purchase order is 100%
          complete.
        </p>
        <div className="sk:mt-2">
          {minUnit
            ? `Shipped Units: ${minUnit} Max Units: ${maxUnit}`
            : `Max Unit: ${maxUnit}`}
        </div>
        {item?.item_upc && (
          <div className="sk:my-4 sk:flex sk:flex-wrap sk:gap-4">
            <div className="sk:min-w-0 sk:flex-[2_1_12rem]">
              <MaskedNumberFormField
                error={errors.shippable_qty}
                label="Completed Units"
                name="shippable_qty"
                required
                control={control}
                inputRight={<FormInputSuffix>Units</FormInputSuffix>}
                maxValue={maxUnit || 200000000}
                defaultValue={null}
              />
            </div>
            <div className="sk:flex-[1_1_6rem]">
              <MuteLabel>Total Units</MuteLabel>
              <div className="sk:my-3">
                {CommonUtility.numberWithCommas(maxUnit)}
              </div>
            </div>
          </div>
        )}

        <div className="sk:flex sk:justify-end sk:gap-2">
          <Button onPress={() => closeModal()}>Cancel</Button>
          <Button type="submit" variant="primary">
            Mark as Complete
          </Button>
        </div>
      </form>
    </AppModal>
  );
}
